import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import AuthContext from '../../../../auth/AuthContext';
import FAQService from '../../../../services/FAQService';
import AlertService from '../../../../services/AlertService';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import ConfirmDialogBox from '../../../confirmDialog/ConfirmDialogBox';
import DownloadIcon from '../../../../assets/icons/download.svg';
import WhiteDownloadIcon from '../../../../assets/icons/WhiteDownloadIcon.svg';
import UploadIcon from '../../../../assets/icons/upload.svg';
import WhiteUploadIcon from '../../../../assets/icons/WhiteUploadIcon.svg';
import { ThemeContext } from '../../../../theme/ThemeContext';
import FAQImportTemplate from '../../../../assets/FAQ Import Template.xlsx';

const FaqMaster = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const authContext = useContext(AuthContext);
    const [question, setQuestion] = useState();
    const [category, setCategory] = useState();
    const [answer, setAnswer] = useState();
    const [errors, setErrors] = useState({});
    const [categories, setCategories] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [faqId, setFaqId] = useState(0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const submitControllerRef = useRef(null);

    useEffect(() => {
        const faqAccess = authContext.getPermissions("faqmaster");
        // if (faqAccess.filter((f) => f.access.includes(1)).length === 0) {
        //     navigate("/");
        // }
        setPermissions({
            showAdd: faqAccess.some(f => f.access.includes(2)),
            showEdit: faqAccess.some(f => f.access.includes(3)),
            showDelete: faqAccess.some(f => f.access.includes(4)),
        });
        setPermissions({
            showAdd: true,
            showEdit: true,
            showDelete: true,
        });
    }, [authContext, navigate]);

const loadFAQ = useCallback(async (signal) => { 
    try {
      const data = await FAQService.Get(
        state?.id,
        authContext,
        signal
      );

      if (signal?.aborted) return;

      if (data.isSuccess) {
        setQuestion(data.data.question);
        setCategory(data.data.typeId);
        setAnswer(data.data.answer);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, state?.id]);

  useEffect(() => {
    const controller = new AbortController(); 
    setLoading(false);
    if (state?.id) {
      setIsEditing(true);
      setFaqId(state?.id);
      loadFAQ(controller.signal); 
    }
    return () => controller.abort(); 
  }, [loadFAQ, authContext, state?.id]);

const loadCategories = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await FAQService.GetFAQCategories(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setCategories(data.data);
        setCategory(data.data[0].value);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController();
    loadCategories(controller.signal);
    return () => controller.abort();
  }, [loadCategories]);



const onSubmitHandler = async (event) => {
  event.preventDefault();

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = {
      question: question,
      typeId: category,
      answer: answer,
    };
    if (faqId > 0) {
      payload.id = faqId;
    }

    const data = await FAQService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("FAQ details are saved successfully!");
      navigate("/masters-faq");
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    console.log(ex);
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

    const onDeleteHandler = async () => {
        setShowDeleteConfirm(false);
        setLoading(true);
        let data = await FAQService.Remove(faqId, authContext);
        if (data.isSuccess) {
            AlertService.success("FAQ details are deleted successfully!");
            navigate("/masters-faq");
        } else {
            AlertService.error(data.errorMessage);
        }
    };

    const onImportHandler = () => {
        navigate(`/masters-faq/import`);
    };

    return (
        <Fragment>
            {loading ? (
                <div className="mt-4 container-fluid">
                    <div className="mb-1">
                        <Skeleton width='10rem' height='2rem'></Skeleton>
                    </div>
                    <div className="mb-4">
                        <Skeleton width='100%' height='3rem'></Skeleton>
                    </div>
                    <div className="mb-1">
                        <Skeleton width='10rem' height='2rem'></Skeleton>
                    </div>
                    <div className="mb-4">
                        <Skeleton width='100%' height='3rem'></Skeleton>
                    </div>
                    <div className="mb-1">
                        <Skeleton width='10rem' height='2rem'></Skeleton>
                    </div>
                    <div className="mb-4">
                        <Skeleton width='100%' height='8rem'></Skeleton>
                    </div>
                    <div className="d-flex">
                        <Skeleton width='7rem' height='3rem'></Skeleton>
                        <Skeleton className='ms-auto' width='7rem' height='3rem'></Skeleton>
                        <Skeleton className='ms-2' width='7rem' height='3rem'></Skeleton>
                    </div>
                </div>
            ) : (<>
                <div className="mb-3 container-fluid mt-3">
                    <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-start align-items-md-center gap-2 gap-md-5">
                        <button className='text-primary d-flex align-items-center card-btn-link'
                            onClick={onImportHandler}>
                            <img
                                src={isDark ? WhiteUploadIcon : UploadIcon}
                                alt="Upload questions"
                                className="me-2"
                            /> Upload Questions</button>
                        <a className=' text-primary'
                            href={FAQImportTemplate}
                            download="FAQ Import Template.xlsx">
                            <img
                                src={isDark ? WhiteDownloadIcon : DownloadIcon}
                                alt="Download template"
                                className="me-2"
                            /> Download Template</a>
                    </div>
           
                <Form className="mt-2" onSubmit={onSubmitHandler}>
                    <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                            Category
                        </Form.Label>
                        <Form.Select
                            className="custom-input custom-input-color"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            disabled={loading}
                        >
                            {categories.length > 0 ? (
                                categories.map((course, i) => (
                                    <option key={i} value={course.value}>
                                        {course.label}
                                    </option>
                                ))
                            ) : (
                                <option value={0}>None</option>
                            )}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                            Question
                        </Form.Label>
                        <Form.Control
                            className={`custom-input custom-input-color ${errors.question ? "is-invalid" : ""}`}
                            type="text"
                            placeholder="Enter Question"
                            value={question}
                            onChange={(e) => {
                                setQuestion(e.target.value);
                                setErrors({ ...errors, question: undefined });
                            }}
                            disabled={loading}
                        />
                        {errors.question && <div className="invalid-feedback d-block">{errors.question}</div>}
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                            Answer
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            className={ `${errors.question ? "is-invalid" : ""} custom-input-color`}
                            placeholder="Enter Answer"
                            value={answer}
                            onChange={(e) => {
                                setAnswer(e.target.value);
                                setErrors({ ...errors, answer: undefined });
                            }}
                            disabled={loading}
                        />
                        {errors.answer && <div className="invalid-feedback d-block">{errors.answer}</div>}
                    </Form.Group>
                    <Row className="mt-5 mb-3">
                        <Col>
                            <div className="d-flex gap-3">
                                {permissions.showDelete && faqId !== 0 && (
                                    <Button
                                        variant="outline-danger"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={loading}
                                        className='px-lg-4 py-2'
                                    >
                                        <FaTrash className='me-2 mb-1' />
                                        Delete
                                    </Button>
                                )}
                                <div className="ms-auto d-flex gap-4">
                                    <Button
                                        variant="outline-secondary"
                                        className="fw-bold px-lg-5 py-2"
                                        onClick={() => navigate("/masters-faq")}
                                        disabled={loading}
                                        type="button"
                                    >
                                        Cancel
                                    </Button>
                                    {((permissions.showAdd && faqId === 0) || (permissions.showEdit && faqId !== 0)) && (
                                        <Button
                                            variant="primary"
                                            className="px-lg-5 py-2 d-flex align-items-center justify-content-center"
                                            disabled={loading}
                                            type="submit"
                                        >
                                            {isEditing ? "Update" : "Submit"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Form>
                     </div>
            </>
            
            )}

            <ConfirmDialogBox
                visible={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                onConfirm={onDeleteHandler}
                title="Are you sure you want to delete this quiz?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
            />
        </Fragment>
    )
}

export default FaqMaster;